/**
 * The session gate. Without a session: the intake as a guest, or sign-in on a
 * device that has held a session before (`D262`), each with a door to the
 * other. With one: the onboarding stepper (T52) while `GET /me/onboarding`
 * still lists steps, and the app (`children`) otherwise. The decision itself
 * is `decideGate` (`gate.ts`).
 *
 * ── NEVER FLASH SIGN-IN AT A SIGNED-IN PERSON ───────────────────────────────
 * While the token store is read, while a device without a session has not
 * said whether it signed in before, and while a signed-in person's onboarding
 * answer is neither restored from the persisted cache nor fetched, the gate
 * paints nothing. On a cold start the persisted answer is shown at once and
 * refetched behind it.
 *
 * ── THE TWO STEPPERS ARE KEYED APART ───────────────────────────────────────
 * The guest's intake and the account's are the same component type at the
 * same place in the tree. Unkeyed, React would keep the guest's instance
 * across the sign-in its own finish triggered, and the account's stepper
 * would never load — never adopt the answers, never commit them.
 */
import { onboardingProgressOptions } from "@repo/client/data/profile";
import type { KeyValueStorage } from "@repo/client/i18n";
import { "auth.guest_answers_discarded" as guestAnswersDiscarded } from "@repo/i18n";
import { useNavigate } from "@tanstack/react-router";
import {
  lazy,
  type ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useState,
} from "react";
import { mergeSearch, useAppSearch } from "../../app/layers";
import type { Search } from "../../app/search";
import { releaseBootSplash } from "../../app/system-chrome";
import { useApi } from "../../data/api";
import { useDataQuery } from "../../data/query-client";
import { useSession } from "../../data/session";
import { preferences } from "../../native";
import { useToast } from "../../ui/overlay";
import { discardGuestIntake } from "../onboarding/hand-off";
import { intakeStore } from "../onboarding/storage";
import { AuthScreen } from "./auth-screen";
import { readSignedInBefore, rememberSignedIn } from "./device-memory";
import {
  decideGate,
  fateOfGuestAnswers,
  type GateDoor,
  type GuestAnswersFate,
} from "./gate";

const OnboardingScreen = lazy(() => import("../onboarding"));
const GuestIntakeScreen = lazy(() =>
  import("../onboarding").then((module) => ({
    default: module.GuestIntakeScreen,
  }))
);

/** The device's own memory (Capacitor Preferences; `localStorage` on the web). */
const deviceStorage: KeyValueStorage = {
  getItem: (key) => preferences.get(key),
  removeItem: (key) => preferences.remove(key),
  setItem: (key, value) => preferences.set(key, value),
};

/** How long the "your answers were not used" notice stays: it explains a
 *  screen the person did not expect, so it outlasts a plain toast. */
const DISCARDED_NOTICE_MS = 8000;

/**
 * Whether this device has held a session (`device-memory.ts`): read once a
 * session is known to be absent, and written whenever one exists.
 */
function useSignedInBefore(hasSession: boolean | null): boolean | null {
  const [before, setBefore] = useState<boolean | null>(null);
  useEffect(() => {
    if (hasSession === null) {
      return;
    }
    if (hasSession) {
      setBefore(true);
      rememberSignedIn(deviceStorage).catch(() => undefined);
      return;
    }
    let cancelled = false;
    readSignedInBefore(deviceStorage).then((stored) => {
      if (!cancelled) {
        setBefore((known) => known === true || stored);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [hasSession]);
  return before;
}

/**
 * The account finished its intake before: the answers this device kept from
 * before the sign-in are dropped, and the person is told when there were any
 * (`D262`). Adoption is the stepper's (`resume.ts`), never this hook's.
 */
function useDiscardStaleGuestAnswers(fate: GuestAnswersFate): void {
  const { notify } = useToast();
  const navigate = useNavigate();
  const stepInUrl = useAppSearch().step !== undefined;
  useEffect(() => {
    if (fate !== "discard") {
      return;
    }
    // The guest's stepper mirrored `?step=`; the app it gave way to has no
    // step to show, so the param does not outlive it.
    if (stepInUrl) {
      navigate({
        replace: true,
        search: (prev: Search) => mergeSearch(prev, { step: undefined }),
        state: (prev) => prev,
        to: "/",
      });
    }
  }, [fate, navigate, stepInUrl]);
  useEffect(() => {
    if (fate !== "discard") {
      return;
    }
    discardGuestIntake(intakeStore)
      .then((hadAnswers) => {
        if (hadAnswers) {
          notify({
            message: guestAnswersDiscarded(),
            timeout: DISCARDED_NOTICE_MS,
            tone: "info",
          });
        }
      })
      .catch(() => undefined);
  }, [fate, notify]);
}

export function SessionGate({ children }: { children: ReactNode }) {
  const api = useApi();
  const { hasSession } = useSession();
  const signedIn = hasSession === true;
  const onboarding = useDataQuery(onboardingProgressOptions(api), {
    enabled: signedIn,
  });
  const signedInBefore = useSignedInBefore(hasSession);
  const [door, setDoor] = useState<GateDoor | null>(null);
  const data = signedIn ? onboarding.data : undefined;
  const decision = decideGate({
    door,
    hasSession,
    onboarding: data,
    // Offline with nothing cached, the query PAUSES rather than fails
    // (`networkMode: "offlineFirst"`); both mean no answer is coming.
    onboardingFailed:
      data === undefined &&
      (onboarding.isError || onboarding.fetchStatus === "paused"),
    signedInBefore,
  });
  useDiscardStaleGuestAnswers(
    fateOfGuestAnswers({ hasSession, onboarding: data })
  );

  // A door is a choice made without a session; the next time there is none
  // (a sign-out), the device's memory decides again.
  useEffect(() => {
    if (signedIn) {
      setDoor(null);
    }
  }, [signedIn]);
  const toSignIn = useCallback(() => setDoor("sign-in"), []);
  const toIntake = useCallback(() => setDoor("intake"), []);

  const decided = decision !== "pending";
  // The splash covers the gate's blank `null` (`D194`): it comes down two
  // frames after the first decided render, not after the first render.
  useEffect(() => {
    if (decided) {
      releaseBootSplash();
    }
  }, [decided]);
  if (decision === "pending") {
    return null;
  }
  if (decision === "auth") {
    return (
      <AuthScreen interceptBack={door === "sign-in"} onNoAccount={toIntake} />
    );
  }
  if (decision === "guest") {
    return (
      <Suspense fallback={null}>
        <GuestIntakeScreen key="guest" onSignIn={toSignIn} />
      </Suspense>
    );
  }
  if (decision === "onboarding") {
    return (
      <Suspense fallback={null}>
        <OnboardingScreen key="account" />
      </Suspense>
    );
  }
  return children;
}
